import { IOrder } from "../types/order/IOrder";

export type OrderSortMode =
  | "server"
  | "created-desc"
  | "first-stop-asc"
  | "completed-desc";

const timestampMillis = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const timestamp = value as any;
  let millis: number;
  if (typeof timestamp.toMillis === "function") millis = timestamp.toMillis();
  else if (typeof timestamp.toDate === "function")
    millis = timestamp.toDate().getTime();
  else {
    const seconds = timestamp.seconds ?? timestamp._seconds;
    const nanos = timestamp.nanoseconds ?? timestamp._nanoseconds ?? 0;
    millis =
      typeof seconds === "number"
        ? seconds * 1000 + nanos / 1e6
        : new Date(timestamp).getTime();
  }
  return Number.isFinite(millis) ? millis : null;
};

const firstStopMillis = (order: IOrder): number | null => {
  const firstStop = [...(order.route ?? [])].sort(
    (a, b) =>
      (a.stopNumber ?? Number.MAX_SAFE_INTEGER) -
      (b.stopNumber ?? Number.MAX_SAFE_INTEGER),
  )[0];
  return timestampMillis(firstStop?.stopDate);
};

const descending = (left: number | null, right: number | null): number => {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return right - left;
};

const ascending = (left: number | null, right: number | null): number => {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return left - right;
};

const orderNumberDescending = (left: IOrder, right: IOrder): number => {
  const leftNumber = Number.parseInt(left.orderNumber ?? "", 10) || 0;
  const rightNumber = Number.parseInt(right.orderNumber ?? "", 10) || 0;
  return rightNumber - leftNumber;
};

export const sortOrders = <T extends IOrder>(
  orders: T[],
  mode: OrderSortMode,
): T[] =>
  mode === "server"
    ? [...orders]
    : [...orders].sort((left, right) => {
        let difference = 0;
        if (mode === "first-stop-asc") {
          difference = ascending(firstStopMillis(left), firstStopMillis(right));
        } else if (mode === "completed-desc") {
          difference = descending(
            timestampMillis(
              left.completedAt ?? left.lastUpdated ?? left.createdAt,
            ),
            timestampMillis(
              right.completedAt ?? right.lastUpdated ?? right.createdAt,
            ),
          );
        } else {
          difference = descending(
            timestampMillis(left.createdAt),
            timestampMillis(right.createdAt),
          );
        }

        return (
          difference ||
          orderNumberDescending(left, right) ||
          (left.id < right.id ? 1 : left.id > right.id ? -1 : 0)
        );
      });
